import { NgModule } from '@angular/core';
import { RouterModule, Routes, PreloadAllModules } from '@angular/router';
import { AuthGuard } from './auth/auth.guard';
import { RoleNames } from './shared/api/types/enums';
import { RoleGuard } from './shared/guards/role.guard';
import { RoutePaths } from './shared/api/types/enums';
import { OrderStatusesResolver } from './order-management/orders/orders.resolver';
import { pricingRoutes } from './routes/pricing.routes';
import { pimRoutes } from './routes/pim.routes';
import { userManagementRoutes } from './routes/user-management.routes';
import { shopAndChannelRoutes } from './routes/shop-and-channels.routes';
import { adminRoutes } from './routes/admin.routes';
import { authRoutes } from './routes/auth.routes';
import { environment } from 'src/environments/environment';

// Exported so route-guards.spec.ts can pin the guards on this table too (NBO-2456).
export const routes: Routes = [
  // Home
  {
    path: '',
    redirectTo: `/home`,
    pathMatch: 'full'
  },
  {
    path: 'home',
    data: {
      title: 'nav.page_titles.home'
    },
    loadComponent: () => import('./home/home.component').then((mod) => mod.HomeComponent),
    canActivate: [AuthGuard]
  },
  // AI standalone components
  {
    path: 'ai-assistants',
    data: {
      title: 'nav.page_titles.sp_ai_assistants'
    },
    loadComponent: () => import('./ai-assistants/ai-assistants.component').then((mod) => mod.AiAssistantsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'action-hub',
    data: {
      title: 'nav.page_titles.sp_action_hub'
    },
    loadComponent: () =>
      import('./ai-sales-room/ai-action-feed/ai-action-feed.component').then((mod) => mod.AiActionFeedComponent),
    canActivate: [AuthGuard]
  },

  // Configuration
  {
    path: RoutePaths.Settings,
    loadChildren: () => import('./settings/settings.module').then((mod) => mod.SettingsModule),
    // NBO-2456: was the last app route with no guard (the agents route was the other, !1278). A cold
    // load built the page before Firebase restored the session; AuthGuard waits for it.
    canActivate: [AuthGuard]
  },

  //  Rule Builder
  {
    path: RoutePaths.RuleBuilder,
    loadChildren: () => import('./rule-builder/rule-builder.module').then((mod) => mod.RuleBuilderModule),
    canActivate: [AuthGuard]
  },

  // Sales
  {
    path: RoutePaths.Companies,
    loadChildren: () => import('./companies/companies.module').then((mod) => mod.CompaniesModule),
    data: { role: [RoleNames.sales, RoleNames.user] },
    canActivate: [AuthGuard, RoleGuard]
  },
  {
    path: RoutePaths.Favorites,
    loadChildren: () => import('src/app/crm/favorites/favorites.module').then((mod) => mod.FavoritesModule),
    data: { role: [RoleNames.sales, RoleNames.shop, RoleNames.product] },
    canActivate: [AuthGuard, RoleGuard]
  },
  {
    path: RoutePaths.ProductCatalogue,
    loadChildren: () => import('./catalog/catalog.module').then((mod) => mod.CatalogModule),
    data: { role: [RoleNames.product, RoleNames.sales] },
    canActivate: [AuthGuard, RoleGuard]
  },

  // Order Management
  {
    path: '',
    loadChildren: () => import('./order-management/orders/order-edit/order-edit.module').then((m) => m.OrderEditModule),
    canActivate: [AuthGuard]
  },
  {
    path: '',
    loadChildren: () => import('./order-management/orders/orders.module').then((mod) => mod.OrdersModule),
    resolve: {
      orderStatuses: OrderStatusesResolver
    },
    canActivate: [AuthGuard]
  },
  {
    path: '',
    loadChildren: () => import('./play-ground/play-ground.module').then((m) => m.PlayGroundModule),
    canActivate: [AuthGuard]
  },
  ...authRoutes,
  ...pimRoutes,
  ...userManagementRoutes,
  ...pricingRoutes,
  ...shopAndChannelRoutes,
  ...adminRoutes,
  // DEV-ONLY specimen of the control vocabulary (NBO-2419): never matched on a production build,
  // never linked from the rail. For measuring the shared controls against /mockups/system/controls.
  {
    path: 'dev/controls',
    canMatch: [() => !environment.production],
    canActivate: [AuthGuard],
    loadComponent: () => import('./dev/controls-specimen/controls-specimen.component').then((m) => m.ControlsSpecimenComponent)
  },
  // DEV-ONLY: the record dialog shell (NBO-2530) in every tier, mode and hard case — the shell has no
  // other consumer until the record-dialog screens adopt it. Never linked from the rail.
  {
    path: 'dev/record-dialog',
    canMatch: [() => !environment.production],
    canActivate: [AuthGuard],
    data: { title: 'nav.page_titles.dev_record_dialog' },
    loadComponent: () =>
      import('./dev/record-dialog-specimen/record-dialog-specimen.component').then((m) => m.RecordDialogSpecimenComponent)
  },
  // DEV-ONLY specimen of the entity page shell (NBO-2532), same rules as the one above: the shell
  // ships switched off — nothing else routes to it until its first screen (NBO-2537) adopts it.
  {
    path: 'dev/entity-page',
    canMatch: [() => !environment.production],
    canActivate: [AuthGuard],
    loadComponent: () => import('./dev/entity-page-specimen/entity-page-specimen.component').then((m) => m.EntityPageSpecimenComponent)
  },
  {
    path: '404',
    loadComponent: () => import('./shared/components/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
    canActivate: [AuthGuard],
    data: {
      title: 'nav.page_titles.sp_not_found'
    }
  },
  { path: '**', redirectTo: '/404', pathMatch: 'full' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule]
})
export class AppRoutingModule {}
